import { showsSiblings } from "../compiler/table"
import { Catalogue, findInstance, findQuestion } from "../types/catalogue"
import {
  Condition,
  isConditionGroup,
  newTabSpecId,
  TableKind,
  TableSpec,
  TabSpecDocument,
} from "../types/tabSpecDocument"
import { conditionPaths } from "./conditions"
import { defaultTablesFor, kindFor, kindsForQuestion } from "./defaults"
import { RAW_NAMES, ShownNames } from "./shownLabels"
import { inSlice, isNumberQuestion, sliceWords } from "./summaries"

/**
 * "book-filter": the spec's book filter, which belongs to no table.
 * "summary": the answer or net a grid summary shows.
 */
export type OptionPlace = "rows" | "net" | "scores" | "summary" | "filter" | "banner" | "book-filter"

export type Finding =
  | { kind: "new-question"; question: string }
  /**
   * The defaults would give the question a table the spec does not have: a
   * new grid row or loop iteration, or a removed summary or row table.
   * `label` is the instance label, "Summary" for the grid summary, "" for a
   * plain question's single table.
   */
  | {
      kind: "new-instance"
      question: string
      instance: string | null
      summary: boolean
      label: string
    }
  | { kind: "missing-question"; question: string; tableId?: string; bannerId?: string }
  | {
      kind: "missing-instance"
      question: string
      tableId: string
      instance: string
      /** The table is of a variable of the data, which was never asked: set only then. */
      source?: "data"
    }
  | {
      kind: "missing-option"
      question: string
      option: string
      where: OptionPlace
      tableId?: string
      bannerId?: string
    }
  | { kind: "new-option"; question: string; option: string; tableId: string }
  | { kind: "type-changed"; question: string; tableId: string; from: TableKind; to: TableKind }
  | { kind: "not-in-data"; question: string; tableId: string }
  /**
   * A table, banner group or filter condition names a variable of the data
   * that the prepared data no longer has, or (`rows`) a grid from sibling
   * containers shown as one table leaves some of its rows out. Not a
   * questionnaire change: the preparation is where to look.
   */
  | {
      kind: "data-variable-gone"
      /** The codebook label of the variable. */
      variable: string
      tableId?: string
      bannerId?: string
      /**
       * A filter condition names it: the condition's path, and the table's
       * question; no question for a condition of the book filter.
       */
      filter?: { path: string; question?: string }
      /** The questionnaire now has a question with the variable's label. */
      inQuestionnaire?: true
      /**
       * A grid from sibling containers shown as one table: the variable is
       * there, but the table leaves rows out. `gone`: the ids of rows not in
       * the loaded data; `leftOut`: the containers beside them that hold no
       * `field` like the others (`CatalogueQuestion.siblings`).
       */
      rows?: { gone: string[]; leftOut: number; field: string }
    }
  /**
   * A project without a questionnaire: the variables of the data (not the
   * weight, and not a grid from sibling containers, which is added
   * deliberately) that no table uses, in codebook order. One finding for all.
   */
  | { kind: "untabled-data"; variables: string[] }

/**
 * True when `t` stands for the same default table as (`instance`, `summary`):
 * the same instance, and both or neither the grid summary.
 */
const matches = (
  t: Pick<TableSpec, "instance" | "kind">,
  instance: string | null,
  summary: boolean
): boolean => (t.instance ?? "") === (instance ?? "") && (t.kind === "grid-summary") === summary

/**
 * Compares the spec with the questionnaire as it is now. Never changes the
 * spec. Findings come in a fixed order: tables in spec order, then banners,
 * then the book filter, then questions and default tables the spec does not
 * have yet, in questionnaire order, then (in a project without a
 * questionnaire, once the data is loaded and the spec has a table) the
 * variables of the data that no table uses. A table or banner group from the
 * data, and a filter's paths, are judged against the prepared data only once
 * it is loaded; a table from the data never counts as a table of a
 * questionnaire question.
 */
export const reconcile = (spec: TabSpecDocument, catalogue: Catalogue): Finding[] => {
  const out: Finding[] = []
  /**
   * The finding of a table or banner group from the data whose variable is
   * not found: its label is now a question's (known without data), or the
   * prepared data does not have it (known only once the data is loaded).
   */
  const dataGone = (
    variable: string,
    at: { tableId: string } | { bannerId: string }
  ): Finding[] =>
    findQuestion(catalogue, variable)
      ? [{ kind: "data-variable-gone", variable, ...at, inQuestionnaire: true }]
      : catalogue.knownPaths
        ? [{ kind: "data-variable-gone", variable, ...at }]
        : []
  /**
   * The findings of the paths of a filter that the loaded data does not
   * have: an answer of a question that no longer exists (`option`), or a
   * path on a variable that is no question's, so on the data (`gone`).
   */
  const filterFindings = (
    filter: Condition,
    known: Set<string>,
    option: (path: string) => Finding,
    gone: (path: string, variable: string) => Finding
  ): Finding[] =>
    conditionPaths(filter).flatMap((p) => {
      if (known.has(p)) return []
      const variable = p.split("//")[0]
      return [findQuestion(catalogue, variable) ? option(p) : gone(p, variable)]
    })

  spec.tables.forEach((t) => {
    const q = findQuestion(catalogue, t.question, t.source)
    if (!q) {
      if (t.source === "data") out.push(...dataGone(t.question, { tableId: t.id }))
      else out.push({ kind: "missing-question", question: t.question, tableId: t.id })
      return
    }
    // A grid from sibling containers as one table uses every row, as a grid
    // summary does: it has no instance of its own.
    const everyRow = t.kind === "grid-summary" || showsSiblings(t, q)
    const instance = everyRow ? null : findInstance(q, t.instance)
    if (!everyRow && !instance) {
      out.push({
        kind: "missing-instance",
        question: q.label,
        tableId: t.id,
        instance: t.instance ?? "",
        ...(t.source === "data" ? { source: "data" as const } : {}),
      })
      return
    }
    if (!kindsForQuestion(q).includes(t.kind))
      out.push({
        kind: "type-changed",
        question: q.label,
        tableId: t.id,
        from: t.kind,
        to: kindFor(q),
      })

    const labels = new Set(q.options.map((o) => o.label))
    const missing = (option: string, where: OptionPlace) =>
      out.push({ kind: "missing-option", question: q.label, option, where, tableId: t.id })

    if (t.rows) {
      const stored = new Set<string>()
      t.rows.forEach((r) => {
        if (r.kind === "option") {
          stored.add(r.label)
          if (!labels.has(r.label)) missing(r.label, "rows")
        }
        if (r.kind === "net")
          r.members.filter((m) => !labels.has(m)).forEach((m) => missing(m, "net"))
      })
      q.options
        .filter((o) => !stored.has(o.label))
        .forEach((o) =>
          out.push({ kind: "new-option", question: q.label, option: o.label, tableId: t.id })
        )
    }
    if (t.scores)
      Object.keys(t.scores)
        .filter((k) => !labels.has(k))
        .forEach((k) => missing(k, "scores"))
    // A grid summary's own choices: the answer or the net members it shows,
    // and the values it is fixed at (`slice`), which the compiler then skips
    // or warns about. A number grid's summary names no answer.
    if (t.kind === "grid-summary") {
      const choice = t.summary
      if (choice?.kind === "answer" && !isNumberQuestion(q) && !labels.has(choice.label))
        missing(choice.label, "summary")
      if (choice?.kind === "net" && !isNumberQuestion(q))
        choice.members.filter((m) => !labels.has(m)).forEach((m) => missing(m, "summary"))
      const fixed = t.slice ? sliceWords(q, t.slice) : ""
      if (t.slice && fixed && !q.instances.some((i) => inSlice(i, t.slice)))
        out.push({ kind: "missing-instance", question: q.label, tableId: t.id, instance: fixed })
    }
    if (catalogue.knownPaths) {
      out.push(
        ...filterFindings(
          t.filter,
          catalogue.knownPaths,
          (p) => ({ kind: "missing-option", question: q.label, option: p, where: "filter", tableId: t.id }),
          (p, variable) => ({
            kind: "data-variable-gone",
            variable,
            tableId: t.id,
            filter: { path: p, question: q.label },
          })
        )
      )
      const inData = everyRow ? q.instances.some((i) => i.inData) : instance?.inData
      if (!inData) out.push({ kind: "not-in-data", question: q.label, tableId: t.id })
      else if (q.siblings && showsSiblings(t, q)) {
        // One finding for the table, whatever rows it leaves out. A container
        // removed from the data altogether is no row any more, and not seen.
        const gone = q.instances.filter((i) => !i.inData).map((i) => i.id)
        const { leftOut, field } = q.siblings
        if (gone.length + leftOut > 0)
          out.push({
            kind: "data-variable-gone",
            variable: q.label,
            tableId: t.id,
            rows: { gone, leftOut, field },
          })
      }
    }
  })

  spec.banners.forEach((b) =>
    b.groups.forEach((g) => {
      // A group of conditions has no question; the banner editor says what
      // its conditions name that is gone.
      if (isConditionGroup(g)) return
      const q = findQuestion(catalogue, g.variable, g.source)
      if (!q) {
        if (g.source === "data") out.push(...dataGone(g.variable, { bannerId: b.id }))
        else out.push({ kind: "missing-question", question: g.variable, bannerId: b.id })
        return
      }
      const labels = new Set(q.options.map((o) => o.label))
      ;(g.points ?? []).forEach((p) => {
        // A range or a condition column names no answer of the question.
        const names = p.kind === "category" ? [p.label] : p.kind === "net" ? p.members : []
        names
          .filter((n) => !labels.has(n))
          .forEach((n) =>
            out.push({
              kind: "missing-option",
              question: q.label,
              option: n,
              where: "banner",
              bannerId: b.id,
            })
          )
      })
    })
  )

  // The book filter belongs to no table: its findings name it instead. An
  // answer the loaded data does not have is reported on its own question.
  if (spec.filter && catalogue.knownPaths)
    out.push(
      ...filterFindings(
        spec.filter,
        catalogue.knownPaths,
        (p) => ({
          kind: "missing-option",
          question: p.split("//")[0],
          option: p,
          where: "book-filter",
        }),
        (p, variable) => ({ kind: "data-variable-gone", variable, filter: { path: p } })
      )
    )

  // The questionnaire tables of each question, grouped once: a lookup per
  // question instead of a pass over every table.
  // Tables left out of the tab book (include: false) still count as present.
  // A table from the data with the same label is another variable's.
  const byQuestion = new Map<string, TableSpec[]>()
  spec.tables.forEach((t) => {
    if (t.source === "data") return
    const list = byQuestion.get(t.question)
    if (list) list.push(t)
    else byQuestion.set(t.question, [t])
  })

  catalogue.questions.forEach((q) => {
    // A grid with no summary and no row tables wants nothing: never report it.
    const wanted = defaultTablesFor(q, spec.defaults, () => "")
    if (wanted.length === 0) return
    const have = byQuestion.get(q.label) ?? []
    if (have.length === 0) {
      out.push({ kind: "new-question", question: q.label })
      return
    }
    // A missing summary is one finding, however many summary tables the
    // defaults give (one per column or loop iteration, one per summary in
    // the list): `tablesForFinding` gives it every one of them.
    let summaryFound = false
    wanted.forEach((w) => {
      const summary = w.kind === "grid-summary"
      if (summary && summaryFound) return
      if (have.some((t) => matches(t, w.instance, summary))) return
      if (summary) summaryFound = true
      out.push({
        kind: "new-instance",
        question: q.label,
        instance: w.instance,
        summary,
        label: summary ? "Summary" : (findInstance(q, w.instance)?.label ?? ""),
      })
    })
  })

  // Without a questionnaire nothing else says which variables still want a
  // table. With no table yet, the empty spec offers the same action. A grid
  // from sibling containers repeats its rows' own variables: it is added
  // deliberately, never counted as wanting a table. Shown as one table, it
  // tables each row's own variable: a row's path is that variable's label.
  if (catalogue.dataOnly && catalogue.knownPaths && spec.tables.length > 0) {
    const tabled = new Set(
      spec.tables
        .filter((t) => t.source === "data")
        .flatMap((t) => {
          const q = findQuestion(catalogue, t.question, "data")
          return q && showsSiblings(t, q) ? [t.question, ...q.instances.map((i) => i.path)] : [t.question]
        })
    )
    const variables = (catalogue.dataVariables ?? [])
      .filter((q) => !q.siblings)
      .map((q) => q.label)
      .filter((label) => label !== spec.data.weight && !tabled.has(label))
    if (variables.length > 0) out.push({ kind: "untabled-data", variables })
  }

  return out
}

/**
 * The default tables that would settle a `new-question` or `new-instance`
 * finding; [] for any other finding or a question no longer in the
 * questionnaire.
 */
export const tablesForFinding = (
  f: Finding,
  spec: TabSpecDocument,
  catalogue: Catalogue,
  makeId: () => string = () => newTabSpecId("table")
): TableSpec[] => {
  if (f.kind !== "new-question" && f.kind !== "new-instance") return []
  const q = findQuestion(catalogue, f.question)
  if (!q) return []
  if (f.kind === "new-question") return defaultTablesFor(q, spec.defaults, makeId)
  // A grid with no summary table gets every summary the defaults give it.
  const wanted = defaultTablesFor(q, spec.defaults, () => "").filter((w) =>
    matches(w, f.instance, f.summary)
  )
  return (f.summary ? wanted : wanted.slice(0, 1)).map((w) => ({ ...w, id: makeId() }))
}

/** The words a researcher knows for each kind of table, for use inside a sentence. */
export const KIND_WORDS: Record<TableKind, string> = {
  categorical: "single choice",
  multi: "multiple choice",
  scale: "scale",
  numeric: "number",
  "grid-summary": "grid summary",
  text: "text",
}

/** "a", "a and b", "a, b and c". */
const listOf = (items: string[]): string =>
  items.length === 1 ? items[0] : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`

const PLACE: Record<OptionPlace, string> = {
  rows: "its rows",
  net: "a net",
  scores: "its scores",
  summary: "its summary",
  filter: "its filter",
  banner: "the banner",
  "book-filter": "the book filter",
}

/**
 * One sentence for the review strip. Variables, options and instances are
 * named as the tables show them (`names`); without shown labels the
 * sentences are those of the raw labels.
 */
export const describeFinding = (f: Finding, names: ShownNames = RAW_NAMES): string => {
  // A filter path keeps its raw form ("Gate//Yes") unless a shown label
  // changes one of its parts: then it reads as the filter line does.
  const pathText = (path: string): string => {
    const words = names.path(path)
    return words === RAW_NAMES.path(path) ? path : words
  }
  switch (f.kind) {
    case "new-question":
      return `${names.variable(f.question)} is in the questionnaire but has no table.`
    case "new-instance": {
      const question = names.variable(f.question)
      if (f.summary) return `${question} has no summary table.`
      const label = f.instance ? names.instance(f.question, f.instance, f.label) : f.label
      return label ? `${question}: "${label}" has no table.` : `${question} has no table.`
    }
    case "missing-question":
      return f.bannerId
        ? `The banner uses ${names.variable(f.question)}, which is no longer in the questionnaire.`
        : `${names.variable(f.question)} is no longer in the questionnaire.`
    case "missing-instance":
      // A variable of the data was never asked: it became a grid.
      return f.instance
        ? `${names.variable(f.question)} no longer has "${names.instance(f.question, f.instance, f.instance)}".`
        : f.source === "data"
          ? `${names.variable(f.question)} is now a grid in the data, so its single table no longer matches.`
          : `${names.variable(f.question)} is now asked per row or per loop, so its single table no longer matches.`
    case "missing-option": {
      if (f.where === "book-filter")
        return `The book filter uses "${pathText(f.option)}", which no longer exists.`
      const option =
        f.where === "filter" ? pathText(f.option) : names.option(f.question, f.option)
      return `${names.variable(f.question)}: "${option}" is used in ${PLACE[f.where]} but no longer exists.`
    }
    case "new-option":
      return `${names.variable(f.question)} has a new option, "${names.option(f.question, f.option)}". It was added to the end of the table.`
    case "type-changed":
      return `${names.variable(f.question)} changed type. The table is set up as ${KIND_WORDS[f.from]}; the question now suits ${KIND_WORDS[f.to]}.`
    case "not-in-data":
      return `${names.variable(f.question)} is not in the loaded data, so its table is skipped.`
    case "data-variable-gone": {
      // The path is neither a question's nor in the data: it may have been
      // either, so the sentence points at neither.
      if (f.filter)
        return f.filter.question === undefined
          ? `The book filter uses "${pathText(f.filter.path)}", which is in neither the questionnaire nor the prepared data.`
          : `${names.variable(f.filter.question)}: "${pathText(f.filter.path)}" is used in its filter but is in neither the questionnaire nor the prepared data.`
      const variable = names.variable(f.variable)
      if (f.rows) {
        const { gone, leftOut, field } = f.rows
        const quoted = gone.map((id) => `"${names.instance(f.variable, id, id)}"`)
        const parts = [
          ...(quoted.length > 0
            ? [`${listOf(quoted)} ${quoted.length === 1 ? "is" : "are"} not in the loaded data`]
            : []),
          ...(leftOut > 0
            ? [
                `${leftOut === 1 ? "1 row" : `${leftOut} rows`} in the data ${
                  leftOut === 1 ? "has" : "have"
                } no "${field}" like the others`,
              ]
            : []),
        ]
        const one = gone.length + leftOut === 1
        return `${variable}: ${parts.join(", and ")}, so the table leaves ${one ? "it" : "them"} out.`
      }
      if (f.inQuestionnaire)
        return f.bannerId
          ? `The banner uses ${variable}, a data variable, but the questionnaire now has a question with that name.`
          : `${variable} is a data variable, but the questionnaire now has a question with that name.`
      return f.bannerId
        ? `The banner uses ${variable}, which is not in the prepared data.`
        : `${variable} is not in the prepared data, so its table is skipped.`
    }
    case "untabled-data":
      return f.variables.length === 1
        ? "1 variable in the data has no table."
        : `${f.variables.length} variables in the data have no table.`
  }
}

export const findingsForTable = (findings: Finding[], tableId: string): Finding[] =>
  findings.filter((f) => "tableId" in f && f.tableId === tableId)

/** `findingsForTable` of every table at once, by table id, in one pass. */
export const findingsByTable = (findings: Finding[]): Map<string, Finding[]> => {
  const out = new Map<string, Finding[]>()
  findings.forEach((f) => {
    if (!("tableId" in f) || typeof f.tableId !== "string") return
    const list = out.get(f.tableId)
    if (list) list.push(f)
    else out.set(f.tableId, [f])
  })
  return out
}
